import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Alert, App, Button, Form, Typography } from 'antd'
import { type RentalOrder } from '@super-mega/server/contracts/order'
import { Authorized } from '@/features/authorization/authorized.tsx'
import { EnterpriseSelector } from '@/features/service-enterprise/index.ts'
import { useTRPC } from '@/trpc/react.ts'
import { OrderEnterprisePanel } from './order-enterprise-panel.tsx'
import type { RentalFormValues } from './rental-form.types.ts'

export function RentalEnterpriseFields({
  existingOrder,
  busy,
  canManageOrder,
  setEnterprisePending,
  onSuccess
}: {
  existingOrder?: RentalOrder
  busy: boolean
  canManageOrder: boolean
  setEnterprisePending: (pending: boolean) => void
  onSuccess: () => void
}) {
  const form = Form.useFormInstance<RentalFormValues>()
  const trpc = useTRPC()
  const cache = useQueryClient()
  const { message } = App.useApp()
  const [opening, setOpening] = useState(false)
  const selection = Form.useWatch('enterpriseSelection', { form, preserve: true })
  const customerId = Form.useWatch('customerId', form)
  const leaseAction = Form.useWatch('leaseAction', form)
  return (
    <section
      className="border-border bg-surface scroll-mt-24 rounded-xl border p-5 shadow-xs"
      id="section-service-enterprise"
    >
      <Typography.Title className="mb-3!" level={5}>
        服务对象及资料
      </Typography.Title>
      {existingOrder &&
      (!existingOrder.canAssociateEnterprise ||
        (existingOrder.subjectId && !selection && customerId === existingOrder.customerId)) ? (
        <>
          <OrderEnterprisePanel
            subjectId={existingOrder.subjectId}
            subjectName={existingOrder.subjectName}
            currentOrder={{ type: 'rental', id: existingOrder.id }}
            disabled={busy}
            canSupplement={canManageOrder}
            onPendingChange={setEnterprisePending}
            onSuccess={onSuccess}
            actions={
              existingOrder.canAssociateEnterprise &&
              canManageOrder && (
                <Authorized permissions={['order.rental.update', 'subject.read']}>
                  <Button
                    disabled={busy}
                    loading={opening}
                    onClick={async () => {
                      if (!existingOrder.subjectId) {
                        return
                      }
                      setOpening(true)
                      setEnterprisePending(true)
                      try {
                        const enterprise = await cache.fetchQuery({
                          ...trpc.order.subjects.detail.queryOptions({ subjectId: existingOrder.subjectId }),
                          staleTime: 0
                        })
                        form.setFieldValue('enterpriseSelection', {
                          mode: 'existing',
                          subjectId: enterprise.id,
                          expectedVersion: enterprise.version
                        })
                      } catch (cause) {
                        void message.error(cause instanceof Error ? cause.message : '企业资料读取失败，请重试')
                      }
                      setOpening(false)
                      setEnterprisePending(false)
                    }}
                  >
                    修改资料
                  </Button>
                </Authorized>
              )
            }
          />
        </>
      ) : (
        <>
          {existingOrder && (
            <Alert
              className="my-3!"
              showIcon
              type="info"
              title="修改本单服务对象及资料"
              description={`本单原企业：${existingOrder.companyName || '未提供'}；原身份：${existingOrder.identityName || '未提供'}。可重新选择企业，或补充所选企业的资料。保存订单后，本单及场地占用将使用新资料；选择暂不关联会清空本单企业与身份关联。`}
            />
          )}
          <Form.Item name="enterpriseSelection" className="mt-4">
            <EnterpriseSelector
              key={customerId}
              customerId={customerId}
              defaultKind={leaseAction === 'setup' ? 'prospectiveCompany' : 'registeredCompany'}
              disabled={busy}
              onPendingChange={setEnterprisePending}
            />
          </Form.Item>
          {existingOrder && customerId === existingOrder.customerId && selection && (
            <Authorized permissions={['order.rental.update', 'subject.read']}>
              <Button disabled={busy} onClick={() => form.setFieldValue('enterpriseSelection', undefined)}>
                取消本次企业修改
              </Button>
            </Authorized>
          )}
        </>
      )}
    </section>
  )
}
